'use client';

import React, { useState, useRef, useCallback } from 'react';

const SERVICE_OPTIONS = [
  'House Washing',
  'Driveway & Concrete Cleaning',
  'Roof Cleaning',
  'Window Cleaning',
  'Solar Panel Cleaning',
  'Gutter Cleaning',
  'Trash Bin Cleaning',
  'Commercial Property Cleaning',
];

interface FormData {
  fullName: string;
  phone: string;
  email: string;
  address: string;
  service: string;
  notes: string;
  photos: File[];
}

const inputStyle: React.CSSProperties = {
  border: '2px solid #e5e7eb',
  background: '#fff',
  borderRadius: '14px',
  padding: '16px 20px',
  fontSize: '16px',
  width: '100%',
  outline: 'none',
  color: '#111827',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  WebkitAppearance: 'none',
};

const inputFocusStyle: React.CSSProperties = {
  border: '2px solid #0ABFBC',
  boxShadow: '0 0 0 4px rgba(10,191,188,0.1)',
};

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        className="block font-semibold text-gray-800 mb-2"
        style={{ fontSize: '15px' }}
      >
        {label}
        {required && <span style={{ color: '#0ABFBC', marginLeft: 4 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

export default function RGPSEstimateForm() {
  const [form, setForm] = useState<FormData>({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    service: '',
    notes: '',
    photos: [],
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = (files: File[]) => {
    const images = files.filter((f) => f.type.startsWith('image/'));
    setForm((prev) => ({ ...prev, photos: [...prev.photos, ...images] }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, []);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const removePhoto = (index: number) => {
    setForm((prev) => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));
  };

  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

      const photoAttachments = await Promise.all(
        form.photos.map(async (file) => ({
          filename: file.name,
          content: await toBase64(file),
          type: file.type,
        }))
      );

      const response = await fetch(`${supabaseUrl}/functions/v1/send-estimate-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          firstName: form.fullName,
          lastName: '',
          phone: form.phone,
          email: form.email,
          address: form.address,
          city: '',
          services: form.service ? [form.service] : [],
          packageId: '',
          squareFootage: 0,
          message: form.notes,
          photoAttachments,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to send estimate request');
      }

      setSubmitted(true);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToHome = () => {
    setSubmitted(false);
    setForm({ fullName: '', phone: '', email: '', address: '', service: '', notes: '', photos: [] });
    setError(null);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ── SUCCESS STATE ──
  if (submitted) {
    return (
      <section
        id="estimate"
        className="py-20 px-4 sm:px-6"
        style={{ background: 'linear-gradient(160deg, #f0fffe 0%, #f8f9fa 50%, #f0fffe 100%)' }}
      >
        <div className="max-w-xl mx-auto">
          <div
            className="bg-white rounded-3xl overflow-hidden text-center"
            style={{ boxShadow: '0 8px 60px rgba(0,0,0,0.08)' }}
          >
            <div className="h-1.5" style={{ background: 'linear-gradient(90deg, #16D4E8, #0ABFBC, #07a3a0)' }} />
            <div className="px-8 py-12 sm:px-12">
              {/* Check icon */}
              <div className="flex justify-center mb-6">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, #d1faf9 0%, #a7f3f1 100%)',
                    boxShadow: '0 4px 24px rgba(10,191,188,0.2)',
                  }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#0ABFBC"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
              </div>

              <h2
                className="font-bold text-gray-900 mb-4"
                style={{ fontSize: 'clamp(1.6rem, 4vw, 2.1rem)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Thank you!
              </h2>

              <p className="text-gray-700 text-base leading-relaxed mb-3">
                Your estimate request has been received. We&apos;ll review your information and get back to you as soon as possible.
              </p>

              <p className="text-gray-400 text-sm leading-relaxed mb-8">
                Please check your Spam or Junk folder if you don&apos;t receive our confirmation email within a few minutes.
              </p>

              <button
                onClick={handleBackToHome}
                className="w-full text-white font-bold text-base py-4 rounded-2xl transition-all"
                style={{
                  background: 'linear-gradient(135deg, #16D4E8 0%, #0ABFBC 50%, #07a3a0 100%)',
                  boxShadow: '0 6px 24px rgba(10,191,188,0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 32px rgba(10,191,188,0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '0 6px 24px rgba(10,191,188,0.3)';
                }}
              >
                ← Back to Home
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ── FORM STATE ──
  return (
    <section
      id="estimate"
      className="py-20 px-4 sm:px-6"
      style={{ background: 'linear-gradient(160deg, #f0fffe 0%, #f8f9fa 50%, #f0fffe 100%)' }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div
            className="inline-flex items-center gap-2 font-bold text-sm px-5 py-2 rounded-full mb-5"
            style={{
              background: 'linear-gradient(135deg, #e0fafa, #b2f0f0)',
              color: '#0ABFBC',
              boxShadow: '0 2px 12px rgba(10,191,188,0.15)',
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            100% Free — No Obligation
          </div>
          <h2
            className="font-bold text-gray-900 mb-3"
            style={{
              fontSize: 'clamp(2rem, 5vw, 2.8rem)',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              lineHeight: 1.15,
            }}
          >
            Get Your Free Estimate
          </h2>
          <p className="text-gray-500 text-lg max-w-md mx-auto leading-relaxed">
            Fill out the short form below — takes less than 30 seconds.
          </p>
        </div>

        {/* Form Card */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-3xl overflow-hidden"
          style={{ boxShadow: '0 8px 60px rgba(0,0,0,0.08), 0 2px 16px rgba(0,0,0,0.04)' }}
        >
          <div className="h-1.5" style={{ background: 'linear-gradient(90deg, #16D4E8, #0ABFBC, #07a3a0)' }} />

          <div className="p-7 sm:p-10 space-y-6">
            {/* Full Name */}
            <Field label="Full Name" required>
              <input
                type="text"
                required
                value={form.fullName}
                onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))}
                placeholder="John Smith"
                style={{
                  ...inputStyle,
                  ...(focusedField === 'fullName' ? inputFocusStyle : {}),
                }}
                onFocus={() => setFocusedField('fullName')}
                onBlur={() => setFocusedField(null)}
              />
            </Field>

            {/* Phone + Email row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Phone Number" required>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="(505) 000-0000"
                  style={{
                    ...inputStyle,
                    ...(focusedField === 'phone' ? inputFocusStyle : {}),
                  }}
                  onFocus={() => setFocusedField('phone')}
                  onBlur={() => setFocusedField(null)}
                />
              </Field>
              <Field label="Email Address" required>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder="john@example.com"
                  style={{
                    ...inputStyle,
                    ...(focusedField === 'email' ? inputFocusStyle : {}),
                  }}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                />
              </Field>
            </div>

            {/* Property Address */}
            <Field label="Property Address" required>
              <input
                type="text"
                required
                value={form.address}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                placeholder="123 Main Street, Albuquerque, NM"
                style={{
                  ...inputStyle,
                  ...(focusedField === 'address' ? inputFocusStyle : {}),
                }}
                onFocus={() => setFocusedField('address')}
                onBlur={() => setFocusedField(null)}
              />
            </Field>

            {/* Service Needed */}
            <Field label="Service Needed" required>
              <div className="relative">
                <select
                  required
                  value={form.service}
                  onChange={(e) => setForm((p) => ({ ...p, service: e.target.value }))}
                  style={{
                    ...inputStyle,
                    paddingRight: '48px',
                    cursor: 'pointer',
                    ...(focusedField === 'service' ? inputFocusStyle : {}),
                  }}
                  onFocus={() => setFocusedField('service')}
                  onBlur={() => setFocusedField(null)}
                >
                  <option value="" disabled>
                    Select a service...
                  </option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <div
                  className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2"
                  style={{ color: '#9ca3af' }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>
            </Field>

            {/* Divider */}
            <div className="border-t border-gray-100" />

            {/* Upload Photos (optional) */}
            <Field label="Upload Photos (Optional)">
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className="cursor-pointer rounded-2xl transition-all text-center py-8 px-6"
                style={{
                  border: dragOver ? '2px dashed #0ABFBC' : '2px dashed #d1d5db',
                  background: dragOver ? 'linear-gradient(135deg, #f0fffe, #e0fafa)' : '#fafafa',
                  boxShadow: dragOver ? '0 0 0 4px rgba(10,191,188,0.08)' : 'none',
                }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                  style={{
                    background: dragOver
                      ? 'linear-gradient(135deg, #16D4E8, #0ABFBC)'
                      : 'linear-gradient(135deg, #e0fafa, #b2f0f0)',
                    color: dragOver ? '#fff' : '#0ABFBC',
                  }}
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="16 16 12 12 8 16" />
                    <line x1="12" y1="12" x2="12" y2="21" />
                    <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" />
                  </svg>
                </div>
                <p className="font-semibold text-gray-700 text-sm mb-1">
                  {form.photos.length > 0
                    ? `${form.photos.length} photo${form.photos.length > 1 ? 's' : ''} selected`
                    : 'Tap to upload photos'}
                </p>
                <p className="text-gray-400 text-xs">JPG, PNG, HEIC — optional but helpful</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handlePhotoChange}
              />
              {form.photos.length > 0 && (
                <div className="mt-3 space-y-2">
                  {form.photos.map((file, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between rounded-xl px-4 py-2.5"
                      style={{ background: '#f0fffe', border: '1.5px solid #b2f0f0' }}
                    >
                      <span className="text-gray-700 text-xs font-medium truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        className="ml-3 flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Field>

            {/* Additional Notes (optional) */}
            <Field label="Additional Notes (Optional)">
              <textarea
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Any details about your property or specific concerns..."
                rows={4}
                style={{
                  ...inputStyle,
                  resize: 'none',
                  ...(focusedField === 'notes' ? inputFocusStyle : {}),
                }}
                onFocus={() => setFocusedField('notes')}
                onBlur={() => setFocusedField(null)}
              />
            </Field>

            {/* Error */}
            {error && (
              <div
                className="rounded-2xl p-4 text-center"
                style={{ background: '#fef2f2', border: '2px solid #fca5a5' }}
              >
                <p className="text-red-700 font-semibold text-sm mb-1">Something went wrong.</p>
                <p className="text-red-600 text-xs">
                  Please email us at{' '}
                  <a href="mailto:rgps505@gmail.com" className="font-bold underline">
                    rgps505@gmail.com
                  </a>{' '}
                  or call{' '}
                  <a href="tel:5059102846" className="font-bold underline">
                    (505) 910-2846
                  </a>
                  .
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full text-white font-bold text-lg py-5 rounded-2xl transition-all flex items-center justify-center gap-3 disabled:opacity-70"
              style={{
                background: loading
                  ? '#9ca3af' :'linear-gradient(135deg, #16D4E8 0%, #0ABFBC 50%, #07a3a0 100%)',
                boxShadow: loading ? 'none' : '0 6px 30px rgba(10,191,188,0.35)',
                letterSpacing: '0.01em',
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 40px rgba(10,191,188,0.45)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = loading ? 'none' : '0 6px 30px rgba(10,191,188,0.35)';
              }}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending Your Request...
                </>
              ) : (
                <>
                  Get My Free Estimate
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>

            <p className="text-center text-gray-400 text-xs">
              Or call us at{' '}
              <a href="tel:5059102846" className="font-semibold hover:underline" style={{ color: '#0ABFBC' }}>
                (505) 910-2846
              </a>
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}
